import type { CandidateTimeline } from "@shared/labs/level-assessment-candidate";
import type {
  EvidenceClaim,
  GetRoundUpdateResponse,
} from "@shared/labs/level-assessment-round-update";
import { useCallback, useEffect, useState } from "react";
import { rpcClient } from "@/lib/rpc";
import { StrengthBars } from "../../results/critical-signals";
import { C, DM, MONO, UI } from "../../results/ui-tokens";
import { Card } from "../../sets/ui";
import { Skeleton } from "../candidate-ui";
import {
  type AxisLabelEntry,
  axisLabelEntries,
  buildEvidenceGrid,
  type EvidenceGrid,
  SPEAKER_LABEL,
} from "./derive";

/**
 * Evidências — the case file's claims explorer: every quote-verified claim the
 * fusion rounds extracted, pivoted competency × rodada. Click a cell to read
 * that intersection's claims — "what did the culture interview say about the
 * technical axes" is a cell here.
 */

/** Minimal projection of the pinned model — only what the grid needs. */
interface PinnedModelAxes {
  model: {
    crossAxes: { id: string; label: string }[];
    disciplines: { id: string; axes: { id: string; label: string }[] }[];
  };
}

interface LoadedState {
  grid: EvidenceGrid;
}

export function EvidenceLens({ data }: { data: CandidateTimeline }) {
  const [state, setState] = useState<LoadedState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<{ axisId: string; col: number } | null>(null);

  const candidateId = data.candidate.id;
  const modelId = data.candidate.modelId;
  const claimRounds = data.rounds.filter((r) => (r.claimsCount ?? 0) > 0);
  const claimRoundsKey = claimRounds.map((r) => r.id).join(",");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rounds = data.rounds.filter((r) => (r.claimsCount ?? 0) > 0);
      const [modelRes, ...updates] = await Promise.all([
        rpcClient.labs.levelAssessmentModelGet({ modelId }) as Promise<PinnedModelAxes>,
        ...rounds.map(
          (r) =>
            rpcClient.labs.levelAssessmentRoundUpdateGet({
              candidateId,
              roundId: r.id,
            }) as Promise<GetRoundUpdateResponse>,
        ),
      ]);
      const axes: AxisLabelEntry[] = axisLabelEntries(modelRes.model);
      const perRound = rounds.flatMap((r, i) => {
        const res = updates[i];
        if (res.status !== "ready") return [];
        return [
          {
            round: {
              roundId: r.id,
              sequence: r.sequence,
              label: r.label ?? `Rodada ${r.sequence}`,
            },
            claims: res.record.claims as EvidenceClaim[],
          },
        ];
      });
      setState({ grid: buildEvidenceGrid(perRound, axes) });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao carregar as evidências.");
    } finally {
      setLoading(false);
    }
  }, [candidateId, modelId, data.rounds]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: claimRoundsKey is the refetch signal for data.rounds
  useEffect(() => {
    void load();
  }, [load, claimRoundsKey]);

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}>
        <Skeleton style={{ height: 120, borderRadius: 14 }} />
        <Skeleton style={{ height: 120, borderRadius: 14 }} />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ ...UI, fontSize: 13.5, color: C.ink50, marginTop: 20 }}>
        Falha ao carregar as evidências: {error}
      </div>
    );
  }
  if (!state || state.grid.rows.length === 0) {
    return (
      <Card style={{ padding: 24, marginTop: 14 }}>
        <span style={{ ...UI, fontSize: 13.5, color: C.ink50 }}>
          Nenhuma evidência textual extraída ainda. Rodadas com CV, transcrição ou texto livre
          alimentam este arquivo.
        </span>
      </Card>
    );
  }

  const { grid } = state;
  const selectedRow = selected ? grid.rows.find((r) => r.axisId === selected.axisId) : null;
  const selectedClaims = selectedRow && selected ? selectedRow.cells[selected.col] : [];
  const selectedColumn = selected ? grid.columns[selected.col] : null;

  return (
    <div style={{ marginTop: 14 }}>
      <Card style={{ padding: "18px 20px", overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th
                style={{
                  ...MONO,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0",
                  textTransform: "uppercase",
                  color: C.ink50,
                  textAlign: "left",
                  padding: "6px 10px 10px 0",
                }}
              >
                Competência
              </th>
              {grid.columns.map((c) => (
                <th
                  key={c.roundId}
                  title={c.label}
                  style={{
                    ...MONO,
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: C.ink70,
                    padding: "6px 8px 10px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  R{c.sequence}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((row) => (
              <tr key={row.axisId} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                <td
                  style={{
                    ...DM,
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: row.scope === "profile" ? C.ink50 : C.ink,
                    padding: "9px 10px 9px 0",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.axisLabel}
                </td>
                {row.cells.map((cell, col) => {
                  const on =
                    selected?.axisId === row.axisId && selected.col === col && cell.length > 0;
                  return (
                    <td
                      key={grid.columns[col].roundId}
                      style={{ textAlign: "center", padding: "4px 6px" }}
                    >
                      {cell.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setSelected(on ? null : { axisId: row.axisId, col })}
                          style={{
                            ...MONO,
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: on ? C.white : C.ink,
                            background: on ? C.ink : C.ink5,
                            border: "none",
                            borderRadius: 8,
                            minWidth: 30,
                            padding: "4px 8px",
                            cursor: "pointer",
                          }}
                          aria-pressed={on}
                          aria-label={`${cell.length} evidências de ${row.axisLabel} na rodada ${grid.columns[col].sequence}`}
                        >
                          {cell.length}
                        </button>
                      ) : (
                        <span style={{ ...MONO, fontSize: 12, color: C.ink20 }}>·</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {selectedRow && selectedColumn && selectedClaims.length > 0 && (
        <Card style={{ padding: "18px 20px", marginTop: 12 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span style={{ ...DM, fontSize: 15, fontWeight: 700, color: C.ink }}>
              {selectedRow.axisLabel}
            </span>
            <span style={{ ...MONO, fontSize: 12, color: C.ink50 }}>
              R{selectedColumn.sequence} · {selectedColumn.label}
            </span>
          </div>
          {selectedClaims.map((claim, i) => (
            <ClaimBlock key={claim.id} claim={claim} last={i === selectedClaims.length - 1} />
          ))}
        </Card>
      )}
    </div>
  );
}

/** One claim: what it establishes, the verbatim quote, and its reading
 *  metadata (strength bars, speaker as a plain word, topic). */
function ClaimBlock({ claim, last }: { claim: EvidenceClaim; last: boolean }) {
  return (
    <div
      style={{
        paddingTop: 14,
        marginTop: 14,
        borderTop: `1px solid ${C.borderSoft}`,
        paddingBottom: last ? 0 : 2,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span
          style={{ ...DM, fontSize: 13.5, fontWeight: 600, color: C.ink, flex: 1, minWidth: 220 }}
        >
          {claim.finding}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flex: "0 0 auto" }}>
          <span style={{ ...UI, fontSize: 11.5, color: C.ink50 }}>
            {SPEAKER_LABEL[claim.speaker]}
          </span>
          <StrengthBars strength={claim.strength} />
        </span>
      </div>
      {claim.quote && (
        <blockquote
          style={{
            ...UI,
            margin: "8px 0 0",
            fontSize: 13,
            color: C.ink70,
            lineHeight: 1.55,
            background: C.paper,
            borderRadius: 10,
            padding: "9px 12px",
          }}
        >
          "{claim.quote}"
        </blockquote>
      )}
      {claim.topic && (
        <div style={{ ...MONO, fontSize: 11.5, color: C.ink50, marginTop: 6 }}>{claim.topic}</div>
      )}
    </div>
  );
}
